/**
 * The document glyph on the map: a page with a folded corner and the format mark, the same paths as the
 * inline Glyph in design/primitives.tsx so a document looks the same moving and sitting in a list. And
 * the documents moving along a flow, as SMIL so they cost nothing and `svg.pauseAnimations()` stops them
 * all at once: an opacity envelope at both ends, a blurred trail a little behind, the format mark swapping
 * where the run happens (with a scale pulse), a split fanning one glyph into many, a merge folding many
 * into one, and failed documents in red that drop off at the end.
 */
import type { PointerEvent, ReactNode } from 'react';
import { cx, glyphPaths } from '../design/primitives.tsx';
import { pointAlong, type RoutedFlowX } from './layout.ts';
import type { MapFlow, MapMotion } from './types.ts';

const t = (v: number) => Number(Math.max(0, Math.min(1, v)).toFixed(3));
const s = (v: number) => Number(v.toFixed(2));

/** The glyph as an SVG group centred on the origin (16 × 20 before `scale`); `failed` draws it in --fail. */
export function DocGlyph({ format, failed, scale = 1, x, y, className }: {
  format: string | null | undefined; failed?: boolean; scale?: number; x?: number; y?: number; className?: string;
}) {
  return <Page format={format} failed={failed} scale={scale} x={x} y={y} className={className} />;
}

/**
 * When a document's animations run: a cycle of `dur` from `begin`, `repeat` times. With `frac` < 1 the lap takes that
 * part of the cycle and the document waits, unseen, for the rest (a lap, then a pause: the rhythm of a rare integration).
 */
interface Timing { dur: string; begin: string; repeat: string; frac?: number }

/** Key times and values for a cycle whose lap is `frac` of it: the lap squeezed into the start, the last value held. */
function inLap(timing: Timing, keyTimes: string, values: string): { keyTimes: string; values: string } {
  const f = timing.frac ?? 1;
  if (f >= 1) return { keyTimes, values };
  const vs = values.split(';');
  return { keyTimes: `${keyTimes.split(';').map((x) => t(Number(x) * f)).join(';')};1`, values: `${values};${vs[vs.length - 1]}` };
}
/**
 * One document train: how long a lap takes and where in the lap it starts (seconds, begin ≤ 0), looping. With `pause`,
 * a lap and then that many seconds without (the rhythm of a rare integration). With `at`, one lap only, starting at
 * that moment of the map's clock (a run that just happened; `key` tells the departures apart).
 */
export interface Train { dur: number; begin: number; pause?: number; at?: number; key?: string }

/**
 * One page. With `out`, the mark turns from `format` into `out` at `swapAt` of the cycle with a pulse;
 * with `drop`, the page sinks away at the end. `children` are the motion and envelope animations.
 */
function Page({ format, out, swapAt = 0.5, failed, drop, scale = 1, x, y, timing, className, children, ...hover }: {
  format: string | null | undefined; out?: string | null; swapAt?: number; failed?: boolean; drop?: boolean; scale?: number;
  x?: number; y?: number; timing?: Timing; className?: string; children?: ReactNode;
  onPointerEnter?: (e: PointerEvent<SVGGElement>) => void; onPointerLeave?: () => void;
}) {
  const g = glyphPaths(format);
  const swapping = timing && out !== undefined && (out ?? '') !== (format ?? '');
  const go = swapping ? glyphPaths(out) : null;
  const swapTimes = `0;${t(swapAt - 0.03)};${t(swapAt + 0.03)};1`;
  const interactive = !!hover.onPointerEnter;
  return (
    <g className={cx('mvm-doc', failed && 'fail', className)} opacity={children ? 0 : 1}
      transform={x !== undefined ? `translate(${s(x)} ${s(y ?? 0)})` : undefined}
      style={interactive ? { pointerEvents: 'auto', cursor: 'pointer' } : undefined} {...hover}>
      {children}
      <g transform={`scale(${s(scale)})`}>
        {go && timing && (
          <animateTransform attributeName="transform" type="scale" additive="sum" {...valuesOf(inLap(timing, `0;${t(swapAt - 0.05)};${t(swapAt)};${t(swapAt + 0.05)};1`, '1;1;1.35;1;1'))}
            dur={timing.dur} begin={timing.begin} repeatCount={timing.repeat} />
        )}
        {drop && timing && (
          <animateTransform attributeName="transform" type="translate" additive="sum" {...valuesOf(inLap(timing, '0;0.86;1', '0 0;0 0;0 14'))}
            dur={timing.dur} begin={timing.begin} repeatCount={timing.repeat} />
        )}
        <path className="page" d={g.page} />
        <path className="fold" d={g.fold} />
        <text className="mark" y={g.markY} fontSize={g.markSize}>
          {g.mark}
          {go && timing && <animate attributeName="opacity" {...valuesOf(inLap(timing, swapTimes, '1;1;0;0'))} dur={timing.dur} begin={timing.begin} repeatCount={timing.repeat} />}
        </text>
        {go && timing && (
          <text className="mark" y={go.markY} fontSize={go.markSize} opacity={0}>
            {go.mark}
            <animate attributeName="opacity" {...valuesOf(inLap(timing, swapTimes, '0;0;1;1'))} dur={timing.dur} begin={timing.begin} repeatCount={timing.repeat} />
          </text>
        )}
      </g>
    </g>
  );
}

function Motion({ pathId, timing, keyPoints, keyTimes }: { pathId: string; timing: Timing; keyPoints?: string; keyTimes?: string }) {
  const k = inLap(timing, keyTimes ?? '0;1', keyPoints ?? '0;1');
  const plain = !keyTimes && (timing.frac ?? 1) >= 1;
  return (
    <animateMotion dur={timing.dur} begin={timing.begin} repeatCount={timing.repeat} calcMode="linear" rotate="0"
      keyPoints={plain ? undefined : k.values} keyTimes={plain ? undefined : k.keyTimes}>
      <mpath href={`#${pathId}`} />
    </animateMotion>
  );
}

function Envelope({ timing, values, keyTimes }: { timing: Timing; values: string; keyTimes: string }) {
  const k = inLap(timing, keyTimes, values);
  return <animate attributeName="opacity" values={k.values} keyTimes={k.keyTimes} dur={timing.dur} begin={timing.begin} repeatCount={timing.repeat} />;
}

const valuesOf = (k: { keyTimes: string; values: string }) => ({ keyTimes: k.keyTimes, values: k.values });

export type GlyphHover = (flow: MapFlow | null, at: { x: number; y: number } | null) => void;

/**
 * The documents of one flow on their way. `pathId` and `fanIds` name the path elements the glyphs follow;
 * `trains` is one cycle per document (Map.tsx staggers the flows that share a column gap against each
 * other). `onHover` gets the flow and the glyph's centre in viewport coordinates while a glyph is pointed at.
 */
export function MovingDocs({ flow, pathId, fanIds, scale, speed, motion, reduced, trains, faded, quiet, live, onHover }: {
  flow: RoutedFlowX; pathId: string; fanIds: string[]; scale: number; speed: number; motion: MapMotion; reduced: boolean;
  trains: Train[]; faded?: boolean;
  /** Overdue: its document stands still at the start of the line, grey. */
  quiet?: boolean;
  /** Live: a document per run that just happened (`trains` with `at`), none otherwise. */
  live?: boolean;
  onHover?: GlyphHover;
}) {
  const fin = flow.formatIn ?? null;
  const fout = flow.formatOut ?? fin;
  // Live: as many documents as runs just happened (none until one does); else at least one
  const n = live ? trains.length : Math.max(1, trains.length);
  const fails = flow.failed > 0 ? Math.max(1, Math.round((n * flow.failed) / Math.max(1, flow.runs))) : 0;
  const hover = onHover
    ? {
      onPointerEnter: (e: PointerEvent<SVGGElement>) => {
        const r = e.currentTarget.getBoundingClientRect();
        onHover(flow, { x: r.left + r.width / 2, y: r.top + r.height / 2 });
      },
      onPointerLeave: () => onHover(null, null),
    }
    : {};
  const style = { opacity: faded ? 0.25 : 1 };

  if (quiet) {
    const p = pointAlong(flow.route, Math.min(0.2, Math.max(0.06, 24 / Math.max(1, flow.length))));
    return (
      <g className="mvm-docs" style={style} data-quiet="">
        <Page format={fin} scale={scale} x={p.x} y={p.y} className="quiet" {...hover} />
      </g>
    );
  }
  if (motion === 'off' || reduced) {
    const p = pointAlong(flow.route, flow.swapAt);
    return (
      <g className="mvm-docs" style={style}>
        <Page format={fout} failed={fails > 0} scale={scale} x={p.x} y={p.y} {...hover} />
      </g>
    );
  }

  const swap = t(Math.max(0.1, Math.min(0.9, flow.swapAt)));
  // A document shows itself only once clear of the node it leaves (it is drawn under the nodes: a glyph
  // half under a pill reads as half drawn) and is gone before it reaches the node it enters
  const clear = Math.min(0.3, Math.max(0.04, (10 * scale + 6) / Math.max(1, flow.length)));
  const tIn = t(clear);
  const tOut = t(1 - clear);
  const docs: ReactNode[] = [];
  for (let k = 0; k < n; k++) {
    const train = trains[k] ?? { dur: 4, begin: 0 };
    const { dur, begin } = train;
    const cycle = dur + (train.pause ?? 0);
    const timing: Timing = train.at !== undefined ? { dur: `${s(dur)}s`, begin: `${s(train.at)}s`, repeat: '1' }
      : { dur: `${s(cycle)}s`, begin: `${s(begin * (cycle / dur))}s`, repeat: 'indefinite', frac: dur / cycle };
    const failed = k < fails;
    const key = `${train.key ?? k}|${speed}|${train.pause ?? ''}`;
    const splitting = !!flow.split && flow.fans.length > 0;
    const merging = !!flow.merge && flow.fans.length > 0;
    if (splitting) {
      // One in, up to the run; then one out per fan, from the run on
      docs.push(
        <Page key={`${key}|in`} format={fin} failed={failed} scale={scale} timing={timing} {...hover}>
          <Motion pathId={pathId} timing={timing} keyPoints={`0;${swap};${swap}`} keyTimes={`0;${swap};1`} />
          <Envelope timing={timing} values="0;1;1;0;0" keyTimes={`0;0.06;${t(swap - 0.01)};${swap};1`} />
        </Page>,
      );
      flow.fans.forEach((fan, i) => {
        const fs = t(Math.max(0.1, Math.min(0.9, fan.swapAt)));
        docs.push(
          <Page key={`${key}|out${i}`} format={fout} failed={failed} scale={scale * 0.9} timing={timing} {...hover}>
            <Motion pathId={fanIds[i]} timing={timing} keyPoints={`${fs};${fs};1`} keyTimes={`0;${swap};1`} />
            <Envelope timing={timing} values="0;0;1;1;0" keyTimes={`0;${swap};${t(swap + 0.01)};0.93;1`} />
          </Page>,
        );
      });
    } else if (merging) {
      flow.fans.forEach((fan, i) => {
        const fs = t(Math.max(0.1, Math.min(0.9, fan.swapAt)));
        docs.push(
          <Page key={`${key}|in${i}`} format={fin} failed={failed} scale={scale * 0.9} timing={timing} {...hover}>
            <Motion pathId={fanIds[i]} timing={timing} keyPoints={`0;${fs};${fs}`} keyTimes={`0;${swap};1`} />
            <Envelope timing={timing} values="0;1;1;0;0" keyTimes={`0;0.06;${t(swap - 0.01)};${swap};1`} />
          </Page>,
        );
      });
      docs.push(
        <Page key={`${key}|out`} format={fout} failed={failed} scale={scale} timing={timing} {...hover}>
          <Motion pathId={pathId} timing={timing} keyPoints={`${swap};${swap};1`} keyTimes={`0;${swap};1`} />
          <Envelope timing={timing} values="0;0;1;1;0" keyTimes={`0;${swap};${t(swap + 0.01)};0.93;1`} />
        </Page>,
      );
    } else {
      // The trail: a faint smear just behind the document, never a second one
      const lag: Timing = train.at !== undefined ? { ...timing, begin: `${s(train.at + 0.04 * dur)}s` }
        : { ...timing, begin: `${s(begin * (cycle / dur) + 0.04 * dur)}s` };
      const g = glyphPaths(fin);
      docs.push(
        <g key={`${key}|trail`} className={cx('mvm-trail', failed && 'fail')} opacity={0}>
          <Motion pathId={pathId} timing={lag} />
          <Envelope timing={lag} values="0;0;1;1;0" keyTimes={`0;${t(clear * 0.6)};${t(clear * 1.2)};${t(Math.min(0.84, 1 - clear * 1.3))};1`} />
          <path className="page" d={g.page} transform={`scale(${s(scale * 0.85)})`} />
        </g>,
        <Page key={key} format={fin} out={fout} swapAt={swap} failed={failed} drop={failed} scale={scale} timing={timing} {...hover}>
          <Motion pathId={pathId} timing={timing} />
          <Envelope timing={timing} values="0;0;1;1;0" keyTimes={`0;${t(clear * 0.6)};${tIn};${failed ? t(Math.min(0.86, tOut)) : tOut};1`} />
        </Page>,
      );
    }
  }
  return <g className="mvm-docs" style={style}>{docs}</g>;
}
